/* eslint-disable react/no-unknown-property */
import React from 'react';
import { ActionIcon, ICON_CLOSE } from '@doveit/bricks';
import cloneDeep from 'lodash.clonedeep';
import objectPath from 'object-path';
import { Agent } from '../../../providers/api/dtos';
import { BrandTemplateAutofillAgentData, BrandTemplateAutofillDecodedDataset, TemplatePropertiesSoldSelectorSlotsConfig } from '../../utils/autofill/types';
import { useAgent, useCurrentAgentSync } from '../../../hooks/use-agent/useAgent';
import * as styles from './TemplateAgentSelector.style';
import AgentAutoComplete from '../../../containers/agent-autocomplete/AgentAutoComplete';
import { AgentStatus } from '../../../domain/types';
import TemplateFieldsForm from '../../components/template-fields-form/TemplateFieldsForm';
import ViewAgentPreview from '../../../agent/containers/view-agent-preview/ViewAgentPreview';
import { mapFieldsConfigToTemplateFieldsInitialValues } from '../../components/template-fields-form/mappers';
import { AGENT_DATA_TEMPLATE_FIELD_CONFIG } from '../../pages/use-brand-template/config/brandTemplateFieldConfig';
import { getFieldSlots } from '../../utils/autofill/autofill';
import AgentReviewsSelector, { AgentReviewsSelectorSlotsConfig } from '../../../agent/containers/agent-reviews-selector/AgentReviewsSelector';
import { mapAgentToAutofillAgentData } from './utils';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import TemplatePropertiesSoldSelector from '../template-properties-sold-selector/TemplatePropertiesSoldSelector';

export interface TemplateAgentSelectorProps {
  agentId?: NonNullable<Agent['id']>,
  decodedDataset: BrandTemplateAutofillDecodedDataset,
  disabled?: boolean,
  onAgentSelected?: (agent: Agent | null) => void,
  onAgentDeselected?: VoidFunction,
  onDataChange?: (agentData: BrandTemplateAutofillAgentData) => void,
}

export const TemplateAgentSelector: React.FC<TemplateAgentSelectorProps> = ({
  agentId,
  decodedDataset,
  disabled,
  onAgentSelected,
  onAgentDeselected,
  onDataChange,
}) => {
  const [id, setId] = React.useState<NonNullable<Agent['id']> | undefined>();
  const { data: currentAgent } = useCurrentAgentSync();
  const { data: agent } = useAgent(id);
  const [agentData, setAgentData] = React.useState<BrandTemplateAutofillAgentData>();

  const canAgentOnlySelectItself = React.useMemo(() => currentAgent && (currentAgent.canManage || []).length === 0, [currentAgent]);

  const coordinator = React.useMemo(
    () => (canAgentOnlySelectItself ? undefined : currentAgent?.email.work),
    [canAgentOnlySelectItself, currentAgent?.email.work],
  );

  const selectableAgentReviews = React.useMemo(
    () => (decodedDataset ? getFieldSlots(decodedDataset.variables, 'agent_data', 'reviews').length : 0),
    [decodedDataset],
  );

  const selectableAgentSoldProperties = React.useMemo(
    () => (decodedDataset ? getFieldSlots(decodedDataset.variables, 'agent_data', 'propertiesSold').length : 0),
    [decodedDataset],
  );

  React.useEffect(() => {
    if (canAgentOnlySelectItself || (currentAgent && agentId && !removeNullAndUndefinedValues([currentAgent?.id, ...(currentAgent?.canManage || []).map((canBeManaged) => canBeManaged.id)]).includes(agentId))) {
      setId(currentAgent?.id);
    } else {
      setId(agentId);
    }
  }, [agentId, canAgentOnlySelectItself, currentAgent]);

  React.useEffect(() => {
    onDataChange?.(agentData || {});
  }, [agentData, onDataChange]);

  React.useEffect(() => {
    setAgentData((prev) => ({
      ...(prev ?? {}),
      ...(agent ? mapAgentToAutofillAgentData(agent) : {}),
    }));
  }, [agent]);

  const onAgentFieldChange = React.useCallback((fieldName: string, value: unknown) => {
    setAgentData((prev) => {
      const next = cloneDeep(prev ?? {});
      objectPath.set(next, fieldName, value);

      return next;
    });
  }, []);

  const filteredAgentFields = React.useMemo(
    () => AGENT_DATA_TEMPLATE_FIELD_CONFIG(agentData).filter(({ name }) => decodedDataset.variables.some((v) => v.raw === name)),
    [agentData, decodedDataset.variables],
  );

  const agentInitialValues = React.useMemo(
    () => mapFieldsConfigToTemplateFieldsInitialValues(filteredAgentFields, agentData),
    [filteredAgentFields, agentData],
  );

  const clearAgentSelection = React.useCallback(() => {
    setAgentData(undefined);
    setId(undefined);

    onAgentDeselected?.();
  }, [onAgentDeselected]);

  const onInternalAgentSelected = React.useCallback((selectedAgent: Agent | null) => {
    setId(selectedAgent?.id);
    onAgentSelected?.(selectedAgent);
  }, [onAgentSelected]);

  const onReviewsChange = React.useCallback((reviews: AgentReviewsSelectorSlotsConfig) => {
    setAgentData((prev) => ({
      ...prev,
      reviews,
    }));
  }, []);

  const onPropertiesSoldChange = React.useCallback((propertiesSold: TemplatePropertiesSoldSelectorSlotsConfig) => {
    setAgentData((prev) => ({
      ...prev,
      propertiesSold,
    }));
  }, []);

  return (
    <>
      {!id && (
        <div css={styles.autocompleteWrapper}>
          <AgentAutoComplete
            aria-label="Seleziona un agente"
            placeholder="Cerca agente"
            status={AgentStatus.ACTIVE}
            onSuggestionSelected={onInternalAgentSelected}
            maxSuggestions={10}
            coordinatedBy={coordinator}
            startFrom={coordinator}
          />
        </div>
      )}
      {id && (
        <div>
          <div css={styles.previewWrapper}>
            <ViewAgentPreview
              agentId={id}
              hiddenFields={{
                fiscalCode: true,
              }}
              primaryActions={!canAgentOnlySelectItself ? [
                <ActionIcon
                  icon={{ path: ICON_CLOSE }}
                  label="Deseleziona l'agente"
                  aria-label="Annulla la selezione dell'agente"
                  disabled={disabled}
                  onClick={clearAgentSelection}
                  size="S"
                />,
              ] : undefined}
            />
          </div>
          <div css={styles.formWrapper}>
            <TemplateFieldsForm
              fields={filteredAgentFields}
              initialValues={agentInitialValues}
              disabled={disabled}
              onFieldChange={onAgentFieldChange}
            />
          </div>

          {(selectableAgentReviews > 0 && agentData?.email) && (
            <div css={styles.extraDataWrapper}>
              <AgentReviewsSelector
                agentEmail={agentData.email}
                decodedDataset={decodedDataset}
                totalSlots={selectableAgentReviews}
                onChange={onReviewsChange}
                disabled={disabled}
                asContent="padded"
              />
            </div>
          )}

          {(selectableAgentSoldProperties > 0 && agentData?.email) && (
            <div css={styles.extraDataWrapper}>
              <div css={styles.divider} />

              <TemplatePropertiesSoldSelector
                agentEmail={agentData.email}
                decodedDataset={decodedDataset}
                totalSlots={selectableAgentSoldProperties}
                onChange={onPropertiesSoldChange}
                disabled={disabled}
                asContent="padded"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};
